import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@lib/utils/utils";
import { SmileIcon } from "@components/ui/smile";

import { IoIosArrowRoundForward } from "react-icons/io";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium cursor-pointer " +
    "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring " +
    "disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        arrow: "group",
        smile: "group",
        expandIcon: "group relative text-primary-foreground bg-primary hover:bg-primary/90",
        ringHover:
          "bg-primary text-primary-foreground transition-all duration-300 hover:bg-primary/90 " +
          "hover:ring-2 hover:ring-primary/90 hover:ring-offset-2",
        shine:
          "text-primary-foreground animate-shine bg-gradient-to-r from-primary " +
          "via-primary/75 to-primary bg-[length:400%_100%] ",
        gooeyRight:
          "text-primary-foreground relative bg-primary z-0 overflow-hidden transition-all duration-500 " +
          "before:absolute before:inset-0 before:-z-10 before:translate-x-[150%] before:translate-y-[150%] " +
          "before:scale-[2.5] before:rounded-[100%] before:bg-gradient-to-r from-zinc-400 " +
          "before:transition-transform before:duration-1000 hover:before:translate-x-[0%] " +
          "hover:before:translate-y-[0%] ",
        gooeyLeft:
          "text-primary-foreground relative bg-primary z-0 overflow-hidden transition-all duration-500 " +
          "after:absolute after:inset-0 after:-z-10 after:translate-x-[-150%] after:translate-y-[150%] " +
          "after:scale-[2.5] after:rounded-[100%] after:bg-gradient-to-l from-zinc-400 " +
          "after:transition-transform after:duration-1000 hover:after:translate-x-[0%] " +
          "hover:after:translate-y-[0%] ",
        linkHover1:
          "relative after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-full " +
          "after:origin-bottom-left after:scale-x-100 hover:after:origin-bottom-right " +
          "hover:after:scale-x-0 after:transition-transform after:ease-in-out after:duration-300",
        linkHover2:
          "relative after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-full " +
          "after:origin-bottom-right after:scale-x-0 hover:after:origin-bottom-left " +
          "hover:after:scale-x-100 after:transition-transform after:ease-in-out after:duration-300",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

interface IconProps {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconPlacement: "left" | "right";
}

interface IconRefProps {
  Icon?: never;
  iconPlacement?: undefined;
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export type ButtonIconProps = IconProps | IconRefProps;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps & ButtonIconProps>(
  ({ className, variant, size, asChild = false, children, Icon, iconPlacement, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {Icon && iconPlacement === "left" && (
          <div
            className={
              "w-0 translate-x-[0%] pr-0 opacity-0 transition-all duration-200 " +
              "group-hover:w-5 group-hover:translate-x-100 group-hover:pr-2 group-hover:opacity-100"
            }
          >
            <Icon />
          </div>
        )}
        <Slottable>{children}</Slottable>
        {Icon && iconPlacement === "right" && (
          <div
            className={
              "w-0 translate-x-full pl-0 opacity-0 transition-all duration-200 " +
              "group-hover:w-5 group-hover:translate-x-0 group-hover:pl-2 group-hover:opacity-100"
            }
          >
            <Icon />
          </div>
        )}
        {/* {children} */}
        {variant === "arrow" && (
          <IoIosArrowRoundForward
            className={"text-muted-foreground text-xl transition-transform duration-300 " + "group-hover:-rotate-45"}
          />
        )}
        {variant === "smile" && <SmileIcon />}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
